'use client';
import { FC } from 'react';
import Image from 'next/image';
import { Text } from '@/components';
import { ProductItemPropTypes } from './Product';
import { IMAGE_URI } from '@/config/endpoints';

interface GiftBoxProductsModalProps {
  giftBoxItem: ProductItemPropTypes;
  giftBoxProducts: ProductItemPropTypes[];
}

const GiftBoxProductsModal: FC<GiftBoxProductsModalProps> = ({
  giftBoxItem,
  giftBoxProducts,
}) => {
  // Filter out gift box items and greeting cards
  const filteredProducts = giftBoxProducts.filter(item => {
    const isNotGiftBox = item?.extension_attributes?.is_gift_box !== '1';
    const isNotGreetingCard =
      item?.extension_attributes?.is_greeting_card !== 1;
    return isNotGiftBox && isNotGreetingCard;
  });

  const totalPrice =
    filteredProducts.reduce((sum, product) => {
      return sum + Number(product.price) * Number(product.qty);
    }, 0) + Number(giftBoxItem?.price || 0);

  return (
    <div className='space-y-2'>
      {/* Gift Box Header */}
      <div className='flex flex-col items-start justify-start gap-y-3 w-full'>
        <span className='text-left text-sm font-semibold text-black/50'>
          GIFT BOX
        </span>
        <div className='flex gap-3 items-start p-3 rounded-lg border border-gray-200 w-full'>
          {/* Image */}
          <div className='w-20 h-20 flex-shrink-0'>
            <Image
              src={`${process.env.NEXT_PUBLIC_CDN_LINK}${IMAGE_URI}${giftBoxItem?.extension_attributes?.product_image}`}
              alt={giftBoxItem?.name}
              className='object-cover rounded w-full h-full'
              width={80}
              height={80}
            />
          </div>

          {/* Main content */}
          <div className='flex flex-col justify-start w-full'>
            <div className='flex flex-col items-end text-right'>
              <p className='text-sm font-medium line-clamp-2'>
                {giftBoxItem?.name}
              </p>

              <p className='text-base font-semibold py-1'>
                ₹{giftBoxItem?.price?.toFixed(0)}
              </p>

              <p className='text-xs text-gray-500'>
                {filteredProducts.length} product
                {filteredProducts.length !== 1 ? 's' : ''} inside
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Products listing */}
      {filteredProducts.length > 0 ? (
        <div className='flex flex-col items-start justify-start gap-y-3 w-full'>
          <span className='text-left text-sm font-semibold text-black/50'>
            PRODUCTS
          </span>
          <ul className='w-full flex flex-col gap-y-2 max-h-60 overflow-y-auto'>
            {filteredProducts.map(product => (
              <li
                key={product.sku}
                className='flex gap-3 items-start p-3 rounded-lg border border-gray-200 w-full'>
                {/* Image */}
                <div className='w-20 h-20 flex-shrink-0'>
                  <Image
                    src={`${process.env.NEXT_PUBLIC_CDN_LINK}${IMAGE_URI}${product?.extension_attributes?.product_image}`}
                    alt={product?.name}
                    className='object-cover rounded w-full h-full'
                    width={80}
                    height={80}
                  />
                </div>

                {/* Main content */}
                <div className='flex flex-col justify-start w-full'>
                  <div className='flex flex-col items-end text-right'>
                    <p className='text-sm font-medium line-clamp-2'>
                      {product?.name}
                    </p>

                    <p className='text-base font-semibold py-1'>
                      ₹{product?.price?.toFixed(0)}
                    </p>

                    <p className='text-xs text-gray-500'>Qty: {product?.qty}</p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className='text-center py-8'>
          <Text className='text-gray-500'>
            No products found in this gift box
          </Text>
        </div>
      )}

      {/* Summary footer */}
      {filteredProducts.length > 0 && (
        <div className='border-t border-gray-200 space-y-2'>
          <div className='border border-fig/20 rounded-lg p-3'>
            <div className='flex flex-col gap-2'>
              <div className='flex justify-between items-center'>
                <span className='text-sm font-semibold'>Total Value</span>
                <span className='text-sm font-semibold'>
                  ₹{totalPrice.toFixed(0)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GiftBoxProductsModal;
